import type { RoomCriteriaData } from './room-criteria-config';
import type { BuildingJsonData } from './types';

type Primitive = string | number | boolean | null | undefined;

interface DesignCriteria {
  lux_level?: number;
  air_rate_per_area?: number;
  air_rate_per_person?: number;
  luminaires_required?: boolean;
  sprinklers_required?: boolean;
  smoke_detectors_required?: boolean;
  pir_detectors_required?: boolean;
  extract_only?: boolean;
  supply_only?: boolean;
  minimum_extract_rate?: number;
  nr_rating?: number;
  occupancy_rate?: number;
  lighting_load?: number;
  small_power_load?: number;
  use_occupancy_rate?: boolean;
  use_air_change_rate?: boolean;
  air_change_rate?: number;
  sprinkler_high_hazard?: boolean;
  [key: string]: Primitive;
}

interface BuildingRoomMinimal {
  uuid?: string;
  room_id?: number;
  room_long_name?: string;
  room_category?: string;
  lux_level?: number;
  summer_internal_dry_bulb?: number;
  winter_internal_dry_bulb?: number;
  design_criteria?: DesignCriteria;
  [key: string]: unknown;
}

interface BuildingFloor {
  rooms?: Record<string, BuildingRoomMinimal>;
  [key: string]: unknown;
}

type BuildingDataWithFloors = BuildingJsonData & {
  floors?: BuildingFloor[];
  default_room_criteria_map?: Record<string, DesignCriteria>;
};

export function mapRoomsFromBuildingJson(
  building: BuildingJsonData | null | undefined,
): RoomCriteriaData[] {
  const buildingWithFloors = building as BuildingDataWithFloors;
  if (!buildingWithFloors || !Array.isArray(buildingWithFloors.floors)) return [];

  const roomCriteriaMap = buildingWithFloors.default_room_criteria_map ?? {};
  const rowsByName = new Map<string, RoomCriteriaData>();

  // Helper function to normalize room category for lookup
  const normalizeRoomCategory = (roomCategory: string): string => {
    return roomCategory.toUpperCase().trim();
  };

  // Helper function to format room category for display
  const formatRoomCategoryForDisplay = (roomCategory: string): string => {
    if (!roomCategory) return '';

    return roomCategory
      .toLowerCase() // Convert to lowercase first
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize first letter of each word
  };

  for (const floor of buildingWithFloors.floors ?? []) {
    const roomsObj = floor?.rooms ?? {};
    for (const key of Object.keys(roomsObj)) {
      const room = roomsObj[key];
      const specs = room.design_criteria ?? {};

      const roomNameRaw = (room.room_long_name ?? '').toString();
      const roomName = roomNameRaw.trim() || `Room-${room.room_id ?? key}`;
      const uniquenessKey = roomName.toLowerCase();
      if (rowsByName.has(uniquenessKey)) continue;

      // Get room category and find corresponding criteria map entry
      const roomCategory = (room.room_category ?? '').toString().trim();
      const roomCriteriaKey = normalizeRoomCategory(roomCategory);
      const roomCriteriaDefault = roomCriteriaMap[roomCriteriaKey] ?? {};

      const pickNumber = (
        fieldName: keyof DesignCriteria,
        value: number | undefined,
      ): { v: number; s: 'specification' | 'default' } => {
        const criteriaValue = roomCriteriaDefault[fieldName] as number | undefined;

        if (value === undefined || value === null || Number.isNaN(value)) {
          // No value in specification, use default from criteria map or 0 as ultimate fallback
          const defaultValue = criteriaValue ?? 0;
          return { v: defaultValue, s: 'default' };
        }

        // Value exists in specification, compare with default
        if (criteriaValue !== undefined && Math.abs(value - criteriaValue) < 0.001) {
          // Specification value matches default, use default value and show green
          return { v: criteriaValue, s: 'default' };
        } else {
          // Specification value differs from default, use specification value and show blue
          return { v: value, s: 'specification' };
        }
      };

      const pickBoolean = (
        fieldName: keyof DesignCriteria,
        value: boolean | undefined,
      ): { v: boolean; s: 'specification' | 'default' } => {
        const criteriaValue = roomCriteriaDefault[fieldName] as boolean | undefined;

        if (value === undefined || value === null) {
          // No value in specification, use default from criteria map or false as ultimate fallback
          const defaultValue = criteriaValue ?? false;
          return { v: defaultValue, s: 'default' };
        }

        // Value exists in specification, compare with default
        if (criteriaValue !== undefined && value === criteriaValue) {
          // Specification value matches default, use default value and show green
          return { v: criteriaValue, s: 'default' };
        } else {
          // Specification value differs from default, use specification value and show blue
          return { v: value, s: 'specification' };
        }
      };

      // Map from Python model fields
      const luxLevel = pickNumber('lux_level', specs.lux_level);
      const airRatePerArea = pickNumber('air_rate_per_area', specs.air_rate_per_area);
      const airRatePerPerson = pickNumber('air_rate_per_person', specs.air_rate_per_person);
      const luminairesRequired = pickBoolean('luminaires_required', specs.luminaires_required);
      const sprinklersRequired = pickBoolean('sprinklers_required', specs.sprinklers_required);
      const smokeDetectorsRequired = pickBoolean(
        'smoke_detectors_required',
        specs.smoke_detectors_required,
      );
      const pirDetectorsRequired = pickBoolean(
        'pir_detectors_required',
        specs.pir_detectors_required,
      );
      const extractOnly = pickBoolean('extract_only', specs.extract_only);
      const supplyOnly = pickBoolean('supply_only', specs.supply_only);
      const minimumExtractRate = pickNumber('minimum_extract_rate', specs.minimum_extract_rate);
      const nrRating = pickNumber('nr_rating', specs.nr_rating);
      const occupancyRate = pickNumber('occupancy_rate', specs.occupancy_rate);
      const lightingLoad = pickNumber('lighting_load', specs.lighting_load);
      const smallPowerLoad = pickNumber('small_power_load', specs.small_power_load);
      const useOccupancyRate = pickBoolean('use_occupancy_rate', specs.use_occupancy_rate);
      const useAirChangeRate = pickBoolean('use_air_change_rate', specs.use_air_change_rate);
      const airChangeRate = pickNumber('air_change_rate', specs.air_change_rate);
      const sprinklerHighHazard = pickBoolean('sprinkler_high_hazard', specs.sprinkler_high_hazard);

      const row: RoomCriteriaData = {
        id: (room.uuid || `${roomName}-${key}`) as string,
        roomName,
        roomCategory: formatRoomCategoryForDisplay(room.room_category ?? ''),
        luxLevel: luxLevel.v,
        airRatePerArea: airRatePerArea.v,
        airRatePerPerson: airRatePerPerson.v,
        luminairesRequired: luminairesRequired.v,
        sprinklersRequired: sprinklersRequired.v,
        smokeDetectorsRequired: smokeDetectorsRequired.v,
        pirDetectorsRequired: pirDetectorsRequired.v,
        extractOnly: extractOnly.v,
        supplyOnly: supplyOnly.v,
        minimumExtractRate: minimumExtractRate.v,
        nrRating: nrRating.v,
        occupancyRate: occupancyRate.v,
        lightingLoad: lightingLoad.v,
        smallPowerLoad: smallPowerLoad.v,
        useOccupancyRate: useOccupancyRate.v,
        useAirChangeRate: useAirChangeRate.v,
        airChangeRate: airChangeRate.v,
        sprinklerHighHazard: sprinklerHighHazard.v,
        valueSource: {
          luxLevel: luxLevel.s,
          airRatePerArea: airRatePerArea.s,
          airRatePerPerson: airRatePerPerson.s,
          luminairesRequired: luminairesRequired.s,
          sprinklersRequired: sprinklersRequired.s,
          smokeDetectorsRequired: smokeDetectorsRequired.s,
          pirDetectorsRequired: pirDetectorsRequired.s,
          extractOnly: extractOnly.s,
          supplyOnly: supplyOnly.s,
          minimumExtractRate: minimumExtractRate.s,
          nrRating: nrRating.s,
          occupancyRate: occupancyRate.s,
          lightingLoad: lightingLoad.s,
          smallPowerLoad: smallPowerLoad.s,
          useOccupancyRate: useOccupancyRate.s,
          useAirChangeRate: useAirChangeRate.s,
          airChangeRate: airChangeRate.s,
          sprinklerHighHazard: sprinklerHighHazard.s,
        },
      };

      rowsByName.set(uniquenessKey, row);
    }
  }

  return Array.from(rowsByName.values()).sort((a, b) => a.roomName.localeCompare(b.roomName));
}

export function mapRoomCriteriaToBuilding(
  criteriaData: RoomCriteriaData[],
  fullBuildingData: BuildingJsonData,
): BuildingJsonData | null {
  if (!fullBuildingData) return null;

  // Create a map of room criteria by room name for quick lookup
  const criteriaByRoomName = new Map<string, RoomCriteriaData>();
  criteriaData.forEach((criteria) => {
    criteriaByRoomName.set(criteria.roomName.toLowerCase(), criteria);
  });

  // Create new building data with immutable updates
  const buildingWithFloors = fullBuildingData as BuildingDataWithFloors;
  const updatedBuilding = {
    ...fullBuildingData,
    floors: buildingWithFloors.floors?.map((floor: BuildingFloor) => {
      if (!floor.rooms) return floor;

      const updatedRooms: Record<string, unknown> = {};
      let hasChanges = false;

      Object.keys(floor.rooms).forEach((roomKey) => {
        const room = floor.rooms![roomKey];
        const roomName = (room.room_long_name || '').toString().trim();
        const criteria = criteriaByRoomName.get(roomName.toLowerCase());

        if (criteria) {
          // Create new room with updated design_criteria
          updatedRooms[roomKey] = {
            ...room,
            design_criteria: {
              ...room.design_criteria,
              lux_level: criteria.luxLevel,
              air_rate_per_area: criteria.airRatePerArea,
              air_rate_per_person: criteria.airRatePerPerson,
              luminaires_required: criteria.luminairesRequired,
              sprinklers_required: criteria.sprinklersRequired,
              smoke_detectors_required: criteria.smokeDetectorsRequired,
              pir_detectors_required: criteria.pirDetectorsRequired,
              extract_only: criteria.extractOnly,
              supply_only: criteria.supplyOnly,
              minimum_extract_rate: criteria.minimumExtractRate,
              nr_rating: criteria.nrRating,
              occupancy_rate: criteria.occupancyRate,
              lighting_load: criteria.lightingLoad,
              small_power_load: criteria.smallPowerLoad,
              use_occupancy_rate: criteria.useOccupancyRate,
              use_air_change_rate: criteria.useAirChangeRate,
              air_change_rate: criteria.airChangeRate,
              sprinkler_high_hazard: criteria.sprinklerHighHazard,
            },
          };
          hasChanges = true;
        } else {
          updatedRooms[roomKey] = room;
        }
      });

      return hasChanges ? { ...floor, rooms: updatedRooms } : floor;
    }),
  } as BuildingJsonData;

  return updatedBuilding;
}
